import { onMounted, onScopeDispose, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { ApiClientError, createSecretOrgClient } from '@architeia/api-client'
import type { WorkspaceDetail, WorkspaceRef, WorkspaceSummary } from '@architeia/api-client'
import { getRuntimeConfig } from '@architeia/config'
import { useAuthStore } from '@/stores/auth'
import { messageRef, type MessageRef } from '@/i18n/messages'

// Mirrors the service's MAX_WORKSPACE_PANELS (services/secret-org content-ref.schema.ts). The web
// package cannot import it (the service never ships a runtime dependency on its source), so the
// server remains the authority — it answers PANEL_LIMIT_REACHED — and this only drives the UI copy.
export const MAX_WORKSPACE_PANELS = 12

export const workspacesEnabled = getRuntimeConfig().featureWorkspacesEnabled

export function parseWorkspaceRef(kind: WorkspaceRef['kind'], input: string): WorkspaceRef | null {
  const value = input.trim()
  if (kind === 'library-discussion') {
    const topicId = Number(value)
    return /^[1-9]\d*$/.test(value) && Number.isSafeInteger(topicId) ? { kind, topicId } : null
  }
  if (kind === 'performer-profile') {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
      ? { kind, profileId: value }
      : null
  }
  if (/^[A-Za-z0-9_-]{1,128}$/.test(value)) return { kind, shareId: value }
  try {
    const url = new URL(value)
    const hosts = new Set([
      'minglemage.com',
      'minglemage.dev.pinakes.de',
      'minglemage.staging.pinakes.de'
    ])
    const match = /^\/share\/([A-Za-z0-9_-]{1,128})\/?$/.exec(url.pathname)
    if (
      url.protocol !== 'https:' ||
      !hosts.has(url.host) ||
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      !match?.[1]
    )
      return null
    return { kind, shareId: match[1] }
  } catch {
    return null
  }
}

export function workspaceSourceUrl(value: string): string | null {
  if (
    /^\/(?:library\/[1-9]\d*-[a-z0-9-]+\/[1-9]\d*-[a-z0-9-]+|performers\/[a-f0-9-]{36})$/i.test(
      value
    )
  )
    return value
  try {
    const url = new URL(value)
    if (url.protocol === 'https:' && !url.username && !url.password) return url.toString()
  } catch {
    return null
  }
  return null
}

function saveMessage(error: unknown): MessageRef {
  if (error instanceof ApiClientError) {
    if (error.code === 'PANEL_ALREADY_PINNED') return messageRef('workspaces.errors.alreadyPinned')
    if (error.code === 'PANEL_LIMIT_REACHED')
      return messageRef('workspaces.errors.panelLimit', { max: MAX_WORKSPACE_PANELS })
    if (error.code === 'SOURCE_NOT_AVAILABLE')
      return messageRef('workspaces.errors.sourceUnavailable')
    if (error.statusCode === 409) return messageRef('workspaces.errors.conflict')
  }
  return messageRef('workspaces.errors.saveFailed')
}

export function useWorkspaceList(): {
  workspaces: Ref<WorkspaceSummary[]>
  state: Ref<'loading' | 'ready' | 'error'>
  loadingMore: Ref<boolean>
  nextCursor: Ref<string | null>
  saving: Ref<boolean>
  error: Ref<MessageRef | null>
  load: () => Promise<void>
  loadMore: () => Promise<void>
  create: (title: string, goal: string) => Promise<string | null>
} {
  const auth = useAuthStore()
  const workspaces = ref<WorkspaceSummary[]>([])
  const state = ref<'loading' | 'ready' | 'error'>('loading')
  const nextCursor = ref<string | null>(null)
  const loadingMore = ref(false)
  const saving = ref(false)
  const error = ref<MessageRef | null>(null)
  let version = 0
  onScopeDispose(() => {
    ++version
  })
  const client = () => createSecretOrgClient({ getAccessToken: () => auth.getAccessToken() })

  async function load(): Promise<void> {
    const current = ++version
    state.value = 'loading'
    error.value = null
    try {
      const page = await client().listWorkspaces()
      if (current !== version) return
      workspaces.value = page.workspaces
      nextCursor.value = page.nextCursor
      state.value = 'ready'
    } catch {
      if (current === version) state.value = 'error'
    }
  }

  async function loadMore(): Promise<void> {
    if (!nextCursor.value || loadingMore.value) return
    const current = version
    loadingMore.value = true
    try {
      const page = await client().listWorkspaces(20, nextCursor.value)
      if (current !== version) return
      const existing = new Set(workspaces.value.map(workspace => workspace.id))
      workspaces.value.push(...page.workspaces.filter(workspace => !existing.has(workspace.id)))
      nextCursor.value = page.nextCursor
    } catch {
      if (current === version) error.value = messageRef('workspaces.errors.loadMoreFailed')
    } finally {
      loadingMore.value = false
    }
  }

  async function create(title: string, goal: string): Promise<string | null> {
    if (saving.value) return null
    saving.value = true
    error.value = null
    try {
      const workspace = await client().createWorkspace(title.trim(), goal.trim() || null)
      return workspace.path
    } catch (cause) {
      error.value = saveMessage(cause)
      return null
    } finally {
      saving.value = false
    }
  }

  onMounted(() => {
    void load()
  })
  return { workspaces, state, loadingMore, nextCursor, saving, error, load, loadMore, create }
}

export type SuggestionAddOutcome =
  'added' | 'duplicate' | 'unavailable' | 'limit' | 'conflict' | 'busy' | 'error'

export function useWorkspaceDetail(id: Ref<string>): {
  workspace: Ref<WorkspaceDetail | null>
  state: Ref<'loading' | 'ready' | 'missing' | 'error' | 'unsupported'>
  title: Ref<string>
  goal: Ref<string>
  pinKind: Ref<WorkspaceRef['kind']>
  sourceInput: Ref<string>
  saving: Ref<boolean>
  error: Ref<MessageRef | null>
  load: () => Promise<void>
  saveMetadata: () => Promise<void>
  pin: () => Promise<void>
  addSuggested: (ref: WorkspaceRef) => Promise<SuggestionAddOutcome>
  move: (index: number, direction: -1 | 1) => Promise<void>
  remove: (panelId: string) => Promise<void>
  deleteWorkspace: () => Promise<boolean>
} {
  const auth = useAuthStore()
  const workspace = ref<WorkspaceDetail | null>(null)
  const state = ref<'loading' | 'ready' | 'missing' | 'error' | 'unsupported'>('loading')
  const title = ref('')
  const goal = ref('')
  const pinKind = ref<WorkspaceRef['kind']>('library-discussion')
  const sourceInput = ref('')
  const saving = ref(false)
  const error = ref<MessageRef | null>(null)
  let version = 0
  onScopeDispose(() => {
    ++version
  })
  const client = () => createSecretOrgClient({ getAccessToken: () => auth.getAccessToken() })

  async function load(): Promise<void> {
    const current = ++version
    const workspaceId = id.value
    if (!workspace.value) state.value = 'loading'
    try {
      const result = await client().getWorkspace(workspaceId)
      if (current !== version) return
      workspace.value = result
      title.value = result.title
      goal.value = result.goal ?? ''
      state.value = 'ready'
      error.value = null
    } catch (cause) {
      if (current !== version) return
      if (cause instanceof ApiClientError && cause.code === 'UNSUPPORTED_LAYOUT') {
        workspace.value = null
        state.value = 'unsupported'
      } else if (cause instanceof ApiClientError && cause.statusCode === 404) {
        workspace.value = null
        state.value = 'missing'
      } else state.value = 'error'
    }
  }

  async function mutate(
    action: (current: WorkspaceDetail) => Promise<WorkspaceDetail>
  ): Promise<void> {
    if (!workspace.value || saving.value) return
    const current = version
    const snapshot = workspace.value
    saving.value = true
    error.value = null
    try {
      const result = await action(snapshot)
      if (current !== version) return
      workspace.value = result
      state.value = 'ready'
    } catch (cause) {
      if (current === version) error.value = saveMessage(cause)
    } finally {
      if (current === version) saving.value = false
    }
  }

  async function saveMetadata(): Promise<void> {
    await mutate(async current => {
      const result = await client().updateWorkspace(
        current.id,
        title.value.trim(),
        goal.value.trim() || null,
        current.revision
      )
      return { ...current, ...result }
    })
  }

  async function pin(): Promise<void> {
    const ref = parseWorkspaceRef(pinKind.value, sourceInput.value)
    if (!ref) {
      error.value = messageRef('workspaces.errors.invalidSource')
      return
    }
    await mutate(current => client().addWorkspacePanel(current.id, ref, current.revision))
    if (!error.value) sourceInput.value = ''
  }

  async function addSuggested(ref: WorkspaceRef): Promise<SuggestionAddOutcome> {
    if (!workspace.value || saving.value) return 'busy'
    const current = version
    const snapshot = workspace.value
    saving.value = true
    // After a 409 the cached revision is stale, so `saving` stays true until the reload resolves —
    // otherwise a second Add could go out with the stale revision and hit a second 409.
    let reloadAfter = false
    try {
      const result = await client().addWorkspacePanel(snapshot.id, ref, snapshot.revision)
      if (current !== version) return 'busy'
      workspace.value = result
      state.value = 'ready'
      return 'added'
    } catch (cause) {
      if (current !== version) return 'busy'
      if (cause instanceof ApiClientError) {
        if (cause.code === 'PANEL_ALREADY_PINNED') return 'duplicate'
        if (cause.code === 'SOURCE_NOT_AVAILABLE') return 'unavailable'
        if (cause.code === 'PANEL_LIMIT_REACHED') return 'limit'
        if (cause.statusCode === 409) {
          reloadAfter = true
          return 'conflict'
        }
      }
      return 'error'
    } finally {
      if (current === version) {
        if (reloadAfter) {
          const reload = load()
          // load() bumps `version` synchronously; only reset `saving` if nothing else did since.
          const reloadVersion = version
          await reload
          if (reloadVersion === version) saving.value = false
        } else saving.value = false
      }
    }
  }

  async function move(index: number, direction: -1 | 1): Promise<void> {
    await mutate(current => {
      const panels = current.panels.map(panel => panel.panelId)
      const target = index + direction
      const source = panels[index]
      const other = panels[target]
      if (!source || !other) return Promise.resolve(current)
      panels[index] = other
      panels[target] = source
      return client().putWorkspaceLayout(current.id, panels, current.revision)
    })
  }

  async function remove(panelId: string): Promise<void> {
    await mutate(current =>
      client().putWorkspaceLayout(
        current.id,
        current.panels.filter(panel => panel.panelId !== panelId).map(panel => panel.panelId),
        current.revision
      )
    )
  }

  async function deleteWorkspace(): Promise<boolean> {
    if (!workspace.value || saving.value) return false
    const current = version
    saving.value = true
    error.value = null
    try {
      await client().deleteWorkspace(workspace.value.id)
      return current === version
    } catch (cause) {
      if (current === version) error.value = saveMessage(cause)
      return false
    } finally {
      if (current === version) saving.value = false
    }
  }

  watch(
    id,
    () => {
      ++version
      workspace.value = null
      saving.value = false
      error.value = null
      sourceInput.value = ''
      void load()
    },
    { immediate: true }
  )
  return {
    workspace,
    state,
    title,
    goal,
    pinKind,
    sourceInput,
    saving,
    error,
    load,
    saveMetadata,
    pin,
    addSuggested,
    move,
    remove,
    deleteWorkspace
  }
}

export function useWorkspacePin(source: Ref<WorkspaceRef>): {
  workspaces: Ref<WorkspaceSummary[]>
  selectedId: Ref<string>
  state: Ref<'loading' | 'ready' | 'error'>
  saving: Ref<boolean>
  message: Ref<MessageRef | null>
  nextCursor: Ref<string | null>
  load: () => Promise<void>
  loadMore: () => Promise<void>
  pin: () => Promise<void>
} {
  const list = useWorkspaceList()
  const selectedId = ref('')
  const saving = ref(false)
  const message = ref<MessageRef | null>(null)
  const auth = useAuthStore()
  let version = 0
  onScopeDispose(() => {
    ++version
  })
  watch(list.error, error => {
    if (error) message.value = error
  })
  watch(
    () => JSON.stringify(source.value),
    () => {
      ++version
      message.value = null
      saving.value = false
    }
  )
  watch(list.workspaces, workspaces => {
    if (!workspaces.some(workspace => workspace.id === selectedId.value))
      selectedId.value = workspaces[0]?.id ?? ''
  })

  async function pin(): Promise<void> {
    const workspace = list.workspaces.value.find(item => item.id === selectedId.value)
    if (!workspace || saving.value) return
    const current = version
    saving.value = true
    message.value = null
    try {
      const result = await createSecretOrgClient({
        getAccessToken: () => auth.getAccessToken()
      }).addWorkspacePanel(workspace.id, source.value, workspace.revision)
      if (current !== version) return
      workspace.revision = result.revision
      message.value = messageRef('workspaces.pin.pinned')
    } catch (cause) {
      if (current === version) message.value = saveMessage(cause)
    } finally {
      if (current === version) saving.value = false
    }
  }

  return {
    workspaces: list.workspaces,
    selectedId,
    state: list.state,
    saving,
    message,
    nextCursor: list.nextCursor,
    load: list.load,
    loadMore: list.loadMore,
    pin
  }
}
